import format from 'date-fns/format';

/**
 * Returns a date in the format YYYY-MM-DD for use in URLs when only the date portion needs to be conveyed
 * @returns
 */
export const urlSafeDate = (date?: Date) =>
  format(date ?? new Date(), 'yyyy-MM-dd');

// Month/day allow 1-2 digits to preserve the old contract for non-padded
// inputs (e.g. `2026-5-13`) that the prior `new Date(...replaceAll)` form
// accepted — bookmarked links and external API callers may still send them.
// Out-of-range values are caught by the rollover check below.
const YYYY_MM_DD = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

/**
 * Parses a `YYYY-MM-DD` URL date string into a local-midnight `Date`.
 *
 * Returns `undefined` when the input is missing, doesn't match the format,
 * or doesn't represent a real calendar date (e.g. trailing garbage,
 * `2026-99-99`, `2026-02-30`). Callers must handle the `undefined` case
 * — passing the result to `Date` methods like `toISOString()` without a
 * guard will crash when the input is malformed.
 */
export const convertUrlStringDate = (
  input: string | null | undefined,
): Date | undefined => {
  if (!input) return undefined;
  const match = YYYY_MM_DD.exec(input);
  if (!match) return undefined;

  const [, year, month, day] = match;
  // Slash form is intentional: `new Date('2026/05/13')` parses as local
  // midnight, whereas `new Date('2026-05-13')` parses as UTC midnight per
  // the ES spec — which would shift the calendar by up to a day for users
  // west of UTC.
  const date = new Date(`${year}/${month}/${day}`);
  if (Number.isNaN(date.getTime())) return undefined;

  // Reject silent rollovers like 2026-02-30 → Mar 2.
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() + 1 !== Number(month) ||
    date.getDate() !== Number(day)
  ) {
    return undefined;
  }

  return date;
};
